import { ChevronDownIcon, Cross2Icon } from '@radix-ui/react-icons';
import { cn } from '@utils/index';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebounce } from '@/hooks/useDebounce';

import {
  displayMetadataValue,
  getMetadataSearchEmptyMessage,
  getVisibleMetadataOptions,
} from './types';

interface MetadataAsyncMultiSelectProps {
  selected: string[];
  onChange: (values: string[]) => void;
  /** Options fetched for the current (debounced) query. */
  options: string[];
  /** Live search string as the user types — lifted so the caller can fetch on it. */
  onQueryChange: (query: string) => void;
  isLoading?: boolean;
  /** List `options` before anything is typed (multiselect typeahead) instead
   * of prompting the user to type. */
  browseWhenEmpty?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  maxCount?: number;
}

/**
 * SearchableMultiSelect variant whose option list is fetched on-demand from the
 * live typed query (AQ-496, AQ-889). Unlike the base SearchableMultiSelect
 * (which filters a static option list internally), this lifts the search string
 * so the caller can hit the value endpoint with `q`; options are also narrowed
 * client-side by the live search so typing feels instant before the debounced
 * fetch lands. Selected values remain visible even when they fall outside the
 * current query result set.
 */
export function MetadataAsyncMultiSelect({
  selected,
  onChange,
  options,
  onQueryChange,
  isLoading = false,
  browseWhenEmpty = false,
  placeholder = 'Select...',
  searchPlaceholder = 'Type 2+ characters to search...',
  className,
  contentClassName,
  disabled = false,
  maxCount = 2,
}: MetadataAsyncMultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebounce(search, 300);

  React.useEffect(() => {
    onQueryChange(debouncedSearch);
  }, [debouncedSearch, onQueryChange]);

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value) {
      setSearch('');
    }
  };

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(item => item !== value));
      return;
    }
    onChange([...selected, value]);
  };

  const handleClear = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    onChange([]);
  };

  const trimmed = search.trim();

  const visibleOptions = React.useMemo(
    () => getVisibleMetadataOptions(options, selected, trimmed),
    [options, selected, trimmed],
  );

  const displayValue = React.useMemo(() => {
    if (selected.length === 0) {
      return placeholder;
    }
    if (selected.length <= maxCount) {
      return selected.map(displayMetadataValue).join(', ');
    }
    return `${selected.length} selected`;
  }, [maxCount, placeholder, selected]);

  const emptyMessage = getMetadataSearchEmptyMessage({
    search: trimmed,
    isLoading,
    browseWhenEmpty,
  });

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn('w-full justify-between', className)}
        >
          <span className="flex-1 truncate text-left font-normal">{displayValue}</span>
          <div className="ml-2 flex shrink-0 items-center">
            {selected.length > 0 && (
              <>
                <Cross2Icon
                  className="mr-2 h-4 w-4 opacity-50 hover:opacity-100"
                  onClick={event => handleClear(event)}
                  aria-label="Clear selection"
                  role="button"
                />
                <div className="mr-2 h-4 w-px bg-border" />
              </>
            )}
            <ChevronDownIcon className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-full min-w-[240px] p-0', contentClassName)} align="start">
        {/* shouldFilter=false: options are filtered by the server query + visibleOptions. */}
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            {visibleOptions.length > 0 && (
              <CommandGroup>
                {visibleOptions.map(value => {
                  const isSelected = selected.includes(value);
                  return (
                    <CommandItem
                      key={value}
                      value={value}
                      onSelect={() => handleSelect(value)}
                      className="gap-3"
                    >
                      <Checkbox checked={isSelected} className="pointer-events-none" />
                      <span className="truncate">{displayMetadataValue(value)}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
